import { Auth } from "aws-amplify";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import config from "aws-exports";

const backendParts = config.aws_user_files_s3_bucket.split("-");
const instanceBackend = backendParts[backendParts.length - 1];
const instanceRegion = config.aws_project_region;
const SESSION_KEY = "meaplatform_network_session_id";

let monitorStarted = false;
let lastReportAt = 0;
let lastReportFingerprint = "";
let sampleCounter = 0;
let sessionIdCache = "";

function ensureSessionId() {
  if (sessionIdCache) {
    return sessionIdCache;
  }
  try {
    if (typeof sessionStorage !== "undefined") {
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) {
        sessionIdCache = existing;
        return existing;
      }
    }
  } catch (error) {
    // Ignore storage access errors and fall back to in-memory session id.
  }

  const generated = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  sessionIdCache = generated;
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(SESSION_KEY, generated);
    }
  } catch (error) {
    // Ignore storage write errors.
  }
  return generated;
}

function classifyNetwork(downlinkMbps, latencyMs, effectiveType, saveData) {
  if (
    saveData === true ||
    effectiveType === "slow-2g" ||
    effectiveType === "2g" ||
    (typeof downlinkMbps === "number" && downlinkMbps < 0.8)
  ) {
    return "Poor";
  }
  if (
    effectiveType === "3g" ||
    (typeof downlinkMbps === "number" && downlinkMbps < 4)
  ) {
    return "Fair";
  }
  if (effectiveType === "4g" || (typeof downlinkMbps === "number" && downlinkMbps >= 4)) {
    return "Good";
  }
  if (typeof downlinkMbps !== "number") {
    if (typeof latencyMs === "number" && latencyMs >= 1200) {
      return "Poor";
    }
    if (typeof latencyMs === "number" && latencyMs >= 500) {
      return "Fair";
    }
  }
  return "Unknown";
}

async function measureNetwork() {
  try {
    if (typeof document !== "undefined" && document.hidden) {
      return null;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return {
        status: "Poor",
        sessionId: ensureSessionId(),
        isAutomation: !!navigator.webdriver,
        downlinkMbps: 0,
        latencyMs: null,
        effectiveType: "offline",
        saveData: false,
        capturedAt: new Date().toISOString(),
      };
    }

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    let downlinkMbps = conn && typeof conn.downlink === "number" ? conn.downlink : null;
    const effectiveType = conn && conn.effectiveType ? conn.effectiveType : "unknown";
    const connRtt = conn && typeof conn.rtt === "number" ? conn.rtt : null;
    const saveData = conn && typeof conn.saveData === "boolean" ? conn.saveData : false;
    const hasNetworkInfoApi = !!(
      conn &&
      (typeof conn.downlink === "number" || typeof conn.rtt === "number" || typeof conn.effectiveType === "string")
    );

    let pingMs = null;
    sampleCounter += 1;
    const shouldPing = !connRtt || sampleCounter % 3 === 0;
    if (shouldPing) {
      const pingStart = performance.now();
      try {
        await fetch(`/favicon.ico?ping=${Date.now()}`, { method: "GET", cache: "no-store" });
        pingMs = Math.round(performance.now() - pingStart);
      } catch (error) {
        pingMs = null;
      }
    }

    const shouldProbeThroughput = !hasNetworkInfoApi && sampleCounter % 2 === 0;
    if (shouldProbeThroughput) {
      try {
        const probeStart = performance.now();
        const response = await fetch(`/logo192.png?netprobe=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
        });
        const buffer = await response.arrayBuffer();
        const elapsedMs = performance.now() - probeStart;
        if (elapsedMs > 0 && buffer.byteLength > 0) {
          downlinkMbps = (buffer.byteLength * 8) / elapsedMs / 1000;
        }
      } catch (error) {
        // Keep downlink as-is when probe fails.
      }
    }

    const latencyMs = pingMs ?? connRtt;
    return {
      status: classifyNetwork(downlinkMbps, latencyMs, effectiveType, saveData),
      sessionId: ensureSessionId(),
      isAutomation: !!navigator.webdriver,
      downlinkMbps: typeof downlinkMbps === "number" ? downlinkMbps : null,
      latencyMs: typeof latencyMs === "number" ? latencyMs : null,
      effectiveType,
      saveData,
      capturedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error("meaplatform network health measure failed", error);
    return null;
  }
}

function currentRoutePath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`;
}

async function reportNetworkHealth(snapshot) {
  const now = Date.now();
  const fingerprint = `${snapshot.status}|${snapshot.effectiveType}|${snapshot.downlinkMbps}|${snapshot.latencyMs}|${snapshot.saveData}|${currentRoutePath()}`;
  const minGapMs = snapshot.status === "Poor" ? 60 * 1000 : 3 * 60 * 1000;
  if (
    lastReportFingerprint === fingerprint &&
    now - lastReportAt < minGapMs
  ) {
    return;
  }

  try {
    const authUser = await Auth.currentAuthenticatedUser({ bypassCache: false }).catch(() => null);
    const accessToken =
      authUser?.signInUserSession?.idToken?.jwtToken ||
      authUser?.signInUserSession?.accessToken?.jwtToken ||
      null;
    if (!accessToken) {
      return;
    }
    const credentials = await Auth.currentCredentials();
    const lambda = new LambdaClient({
      credentials: Auth.essentialCredentials(credentials),
      region: instanceRegion,
    });
    const payload = {
      body: JSON.stringify({
        target: "report-network-health",
        access_token: accessToken,
        networkHealth: {
          ...snapshot,
          routePath: currentRoutePath(),
        },
      }),
    };

    const result = await lambda.send(
      new InvokeCommand({
        FunctionName: `instanceHandlerLight-${instanceBackend}`,
        InvocationType: "Event",
        Payload: JSON.stringify(payload),
      })
    );
    if (result?.StatusCode && result.StatusCode >= 400) {
      throw new Error(`Network health invoke failed with status ${result.StatusCode}`);
    }
    lastReportAt = now;
    lastReportFingerprint = fingerprint;
  } catch (error) {
    console.error("meaplatform network health report failed", error);
  }
}

export function bootstrapNetworkHealthMonitor() {
  if (monitorStarted || typeof window === "undefined") {
    return;
  }
  monitorStarted = true;

  const monitor = async () => {
    const snapshot = await measureNetwork();
    if (!snapshot) return;
    await reportNetworkHealth(snapshot);
  };

  monitor();
  const intervalId = window.setInterval(monitor, 30000);

  const onVisibilityChange = () => {
    if (!document.hidden) {
      monitor();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  window.addEventListener("beforeunload", () => {
    window.clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  });
}
